import {
  Item,
  LayoutRoot,
  Navbar,
  Section,
  Sidebar,
  SidebarSection
} from '@inappsis/react-inappsis-ui';
import { Box, Toolbar } from '@mui/material';
import { AccountButton } from 'layouts/NavbarElements';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import type { TFunction } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { checkRoles } from 'shared/utils/functions';
import { adminSection, generalSections, userSection } from './LayoutSections';
import { Logo } from './Logo';

interface LayoutProps {
  children?: ReactNode;
}

const removeDuplicateItemHandler = (
  sections: Section[],
  currentSections: Section[]
) => {
  return currentSections.map((currentSection) => {
    currentSection.items.forEach((currentItem: Item, index: number) => {
      const duplicate = sections.find((section) =>
        section.items.find((item: Item) => item.title === currentItem.title)
      );
      if (duplicate) currentSection.items.splice(index, 1);
    });
    return currentSection;
  });
};

const adminNavConfig = (t: TFunction): Section[] => [
  generalSections(t),
  adminSection(t)
];

const getSections = (t: TFunction): Section[] => {
  if (checkRoles(['ADMIN'])) return adminNavConfig(t);

  const currentSections: Section[] = [generalSections(t)];

  if (checkRoles(['USER'])) {
    currentSections.push(
      ...removeDuplicateItemHandler(currentSections, [userSection(t)])
    );
  }

  return currentSections;
};

export const Layout: FC<LayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const location = useLocation();
  const sections = useMemo(() => getSections(t), [t]);

  return (
    <>
      <LayoutRoot>
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          {children}
        </Box>
      </LayoutRoot>
      <Navbar onOpenSidebar={(): void => setIsSidebarOpen(true)}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: 64,
            left: 0,
            px: 2
          }}
        >
          {/* Others navbar buttons should be added here */}
          <AccountButton />
        </Toolbar>
      </Navbar>
      <Sidebar
        logo={
          <Logo
            sx={{
              maxHeight: 120,
              maxWidth: '100%'
            }}
          />
        }
        onClose={(): void => setIsSidebarOpen(false)}
        open={isSidebarOpen}
      >
        {sections.map((section) => (
          <SidebarSection
            key={section.title}
            path={location.pathname}
            sx={{
              mt: 2,
              '& + &': {
                mt: 2
              }
            }}
            {...section}
          />
        ))}
      </Sidebar>
    </>
  );
};
